// BE#366 — Chrome-extension side of the `agent-vision` image downsize (canvas).
//
// Produces a small JPEG derivative of an inbound image for Gemini vision, sized by the SHARED
// bt-common ladder (schemas/wa/agent-vision-resize) — the SAME target dimensions + quality ladder
// the Baileys pod's `sharp` path uses, so the two engines' outputs are equivalent. Uses
// `createImageBitmap(blob, { imageOrientation:'from-image' })` so EXIF orientation is baked in
// (plain `new Image()`+drawImage does not reliably apply it); JPEG encode strips EXIF. HEIC/WebP/
// PNG/GIF-first-frame all normalize to baseline JPEG. Returns null on any failure → caller keeps
// full/placeholder (a hard-failure fallback, never a size one).

import {
    computeFitDimensions,
    AGENT_VISION_ENCODE_LADDER,
    AGENT_VISION_TARGET_MAX_BYTES,
    AGENT_VISION_OUTPUT_MIME,
} from '../../bt-common/schemas/wa/agent-vision-resize';

export interface AgentVisionResult {
    dataBase64: string;
    mimeType: string; // image/jpeg
}

async function encodeCanvasToJpeg(
    bitmap: ImageBitmap,
    width: number,
    height: number,
    quality: number,
): Promise<Blob | null> {
    // Prefer OffscreenCanvas (works off the main thread / in workers); fall back to a DOM canvas.
    if (typeof OffscreenCanvas !== 'undefined') {
        const canvas = new OffscreenCanvas(width, height);
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(bitmap, 0, 0, width, height);
        return canvas.convertToBlob({ type: AGENT_VISION_OUTPUT_MIME, quality });
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, width, height);
    return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, AGENT_VISION_OUTPUT_MIME, quality));
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
}

/**
 * Downsize an inbound image Blob to the agent-vision derivative. Walks the shared ladder and
 * returns the first encode under the byte ceiling, else the last (smallest) — always bounded.
 * Returns null if the bytes aren't a decodable image (caller falls back). Never throws.
 */
export async function resizeImageForAgentVision(blob: Blob): Promise<AgentVisionResult | null> {
    let bitmap: ImageBitmap;
    try {
        bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
    } catch {
        return null;
    }
    try {
        let last: AgentVisionResult | null = null;
        for (const step of AGENT_VISION_ENCODE_LADDER) {
            const { width, height } = computeFitDimensions(bitmap.width, bitmap.height, step.maxEdge);
            const out = await encodeCanvasToJpeg(bitmap, width, height, step.quality / 100);
            if (!out) continue;
            const dataBase64 = await blobToBase64(out);
            last = { dataBase64, mimeType: AGENT_VISION_OUTPUT_MIME };
            if (out.size <= AGENT_VISION_TARGET_MAX_BYTES) return last;
        }
        return last;
    } catch {
        return null;
    } finally {
        bitmap.close?.();
    }
}
